import { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  updateOfferteServiceAddress,
  type OfferteServiceAddress,
  type OfferteAddress,
} from '../../lib/offertes';
import { useAddressLookup, isValidPostcode } from '../../hooks/useAddressLookup';

interface ServiceAddressDialogProps {
  isOpen: boolean;
  leadId: string;
  initial: OfferteServiceAddress;
  /** Postcode van het klant-/bedrijfsadres — toont een hint als ophalen afwijkt. */
  customerPostalCode: string;
  onClose: () => void;
  onSaved: () => void;
}

function formatPostcode(raw: string): string {
  const clean = raw.replace(/\s/g, '').toUpperCase();
  if (clean.length >= 5 && /^\d{4}[A-Z]/.test(clean)) {
    return `${clean.slice(0, 4)} ${clean.slice(4, 6)}`;
  }
  return clean;
}

function parseStreet(raw: string): { streetName: string; houseNumber: string } {
  const match = raw.trim().match(/^(.+?)\s+(\d+\S*)$/);
  if (match && match[1] && match[2]) {
    return { streetName: match[1].trim(), houseNumber: match[2].trim() };
  }
  return { streetName: raw.trim(), houseNumber: '' };
}

function first4(pc: string): string {
  return (pc.replace(/\D/g, '').slice(0, 4)) || '';
}

export interface SubFormValue {
  street: string;
  postalCode: string;
  city: string;
  toevoeging: string;
  valid: boolean;
}

/**
 * Eén adres-invoerblok met PDOK-postcodelookup (postcode + huisnr → straat/plaats),
 * met handmatige fallback. Surface't de actuele waarde + validiteit naar de parent.
 */
function AddressSubForm({
  initial,
  disabled,
  onChange,
}: {
  initial: OfferteAddress;
  disabled: boolean;
  onChange: (v: SubFormValue) => void;
}) {
  const parsed = parseStreet(initial.street);
  const [postalCode, setPostalCode] = useState<string>(initial.postalCode);
  const [houseNumber, setHouseNumber] = useState<string>(parsed.houseNumber);
  const [toevoeging, setToevoeging] = useState<string>(initial.toevoeging ?? '');
  const [streetName, setStreetName] = useState<string>(parsed.streetName);
  const [city, setCity] = useState<string>(initial.city);
  const [forceManual, setForceManual] = useState<boolean>(false);

  const { lookup, result, isLoading, isManualMode, error: lookupError } = useAddressLookup();

  useEffect(() => {
    if (postalCode && houseNumber) lookup(postalCode, houseNumber);
  }, [postalCode, houseNumber, lookup]);

  useEffect(() => {
    if (!result) return;
    setStreetName(result.street);
    setCity(result.city);
  }, [result]);

  useEffect(() => {
    const valid =
      isValidPostcode(postalCode) &&
      houseNumber.trim().length > 0 &&
      streetName.trim().length > 0 &&
      city.trim().length > 0;
    onChange({
      street: `${streetName.trim()} ${houseNumber.trim()}`.trim(),
      postalCode: formatPostcode(postalCode),
      city: city.trim(),
      toevoeging: toevoeging.trim(),
      valid,
    });
    // onChange bewust niet in deps — parent geeft een nieuwe ref per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postalCode, houseNumber, toevoeging, streetName, city]);

  const postcodeValid = isValidPostcode(postalCode);
  const haveKey = postcodeValid && houseNumber.trim().length > 0;
  const showManualFields = haveKey && (isManualMode || forceManual || (!isLoading && !result));

  return (
    <>
      <div className="grid grid-cols-6 gap-2">
        <input
          type="text"
          placeholder="Postcode"
          value={postalCode}
          onChange={(e) => setPostalCode(formatPostcode(e.target.value))}
          autoComplete="postal-code"
          disabled={disabled}
          className="col-span-2 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <input
          type="text"
          placeholder="Huisnr"
          value={houseNumber}
          onChange={(e) => setHouseNumber(e.target.value)}
          autoComplete="address-line2"
          disabled={disabled}
          className="col-span-2 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <input
          type="text"
          placeholder="Toev."
          value={toevoeging}
          onChange={(e) => setToevoeging(e.target.value)}
          disabled={disabled}
          className="col-span-2 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {haveKey && isLoading && (
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <div className="w-3 h-3 border-2 border-slate-200 border-t-indigo-600 rounded-full animate-spin" />
          Adres zoeken...
        </div>
      )}

      {result && !forceManual && !isManualMode && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-600">
            {result.street}, {result.city}
          </span>
          <button
            type="button"
            onClick={() => setForceManual(true)}
            disabled={disabled}
            className="text-indigo-600 hover:underline"
          >
            Klopt niet?
          </button>
        </div>
      )}

      {lookupError && !result && (
        <p className="text-xs text-amber-600">{lookupError} — vul handmatig in</p>
      )}

      {showManualFields && (
        <div className="grid grid-cols-2 gap-2">
          <input
            type="text"
            placeholder="Straat"
            value={streetName}
            onChange={(e) => setStreetName(e.target.value)}
            autoComplete="address-line1"
            disabled={disabled}
            className="px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <input
            type="text"
            placeholder="Plaats"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            autoComplete="address-level2"
            disabled={disabled}
            className="px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      )}
    </>
  );
}

const EMPTY: SubFormValue = { street: '', postalCode: '', city: '', toevoeging: '', valid: false };

export function ServiceAddressDialog({
  isOpen,
  leadId,
  initial,
  customerPostalCode,
  onClose,
  onSaved,
}: ServiceAddressDialogProps) {
  const hasPickup = !!initial.pickup.postalCode || !!initial.pickup.street;

  const [enablePickup, setEnablePickup] = useState<boolean>(hasPickup);
  const [enableDelivery, setEnableDelivery] = useState<boolean>(initial.useAlternateDelivery);
  const [pickup, setPickup] = useState<SubFormValue>(EMPTY);
  const [delivery, setDelivery] = useState<SubFormValue>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset wanneer de dialog opnieuw opent (verse initiële waarden).
  useEffect(() => {
    if (isOpen) {
      setEnablePickup(hasPickup);
      setEnableDelivery(initial.useAlternateDelivery);
      setPickup(EMPTY);
      setDelivery(EMPTY);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const pickupOk = !enablePickup || pickup.valid;
  const deliveryOk = !enableDelivery || delivery.valid;
  const canSubmit = pickupOk && deliveryOk;

  const pickupRegionDiffers =
    enablePickup &&
    pickup.valid &&
    first4(customerPostalCode) !== '' &&
    first4(pickup.postalCode) !== first4(customerPostalCode);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await updateOfferteServiceAddress(leadId, {
        pickup: enablePickup
          ? {
              street: pickup.street,
              postalCode: pickup.postalCode,
              city: pickup.city,
              toevoeging: pickup.toevoeging || undefined,
            }
          : null,
        delivery: enableDelivery
          ? {
              street: delivery.street,
              postalCode: delivery.postalCode,
              city: delivery.city,
              toevoeging: delivery.toevoeging || undefined,
            }
          : null,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Ophaal- en afleveradres</h2>
          <p className="text-xs text-slate-500 mt-1">
            Het ophaaladres bepaalt welke ophaaldagen de klant ziet. Laat het leeg om
            het klant-/bedrijfsadres te gebruiken. Het factuuradres verandert niet.
          </p>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Ophaaladres */}
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={enablePickup}
                onChange={(e) => setEnablePickup(e.target.checked)}
                disabled={submitting}
              />
              <span className="text-sm font-medium text-slate-700">Apart ophaaladres</span>
            </label>
            {enablePickup ? (
              <AddressSubForm
                initial={initial.pickup}
                disabled={submitting}
                onChange={setPickup}
              />
            ) : (
              <p className="text-xs text-slate-500">
                Ophalen op het klant-/bedrijfsadres ({customerPostalCode || 'geen postcode'}).
              </p>
            )}
            {pickupRegionDiffers && (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                Let op: het ophaaladres ligt in een ander postcodegebied dan het
                klantadres. De klant ziet straks de ophaaldagen voor het ophaaladres.
              </div>
            )}
          </div>

          <div className="border-t border-slate-100" />

          {/* Afleveradres */}
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={enableDelivery}
                onChange={(e) => setEnableDelivery(e.target.checked)}
                disabled={submitting}
              />
              <span className="text-sm font-medium text-slate-700">Afwijkend afleveradres</span>
            </label>
            {enableDelivery ? (
              <AddressSubForm
                initial={initial.delivery}
                disabled={submitting}
                onChange={setDelivery}
              />
            ) : (
              <p className="text-xs text-slate-500">Afleveren op het ophaaladres.</p>
            )}
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Sluiten
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !canSubmit}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              submitting || !canSubmit
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            )}
          >
            {submitting ? 'Opslaan…' : 'Opslaan'}
          </button>
        </div>
      </div>
    </div>
  );
}
